import { useQueryClient } from '@tanstack/react-query';
/**
 * ============================================================
 * KEJJA OS — OnboardingPage (with Create/Join choice)
 * Step 0: Choose Create or Join
 * Step 1-3: (only for Create) Name → Location → Review
 * Join path: enter code → accept → redirect to /dashboard
 * ============================================================
 */

import { useState, useMemo, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Home as HomeIcon, Loader2, Sparkles, AlertCircle,
  PlusCircle, KeyRound, Check,
} from 'lucide-react';

import { useAuth } from '@/hooks/useAuth';
import { useCreateHome } from '@/hooks/useHomes';
import { useAcceptInvite } from '@/hooks/useSettings';
import { COUNTRIES, findCountry } from '@/lib/countries';
import { CURRENCIES, findCurrency } from '@/lib/currencies';
import { TIMEZONES, timezonesByRegion } from '@/lib/timezones';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type Flow = 'choose' | 'create' | 'join';
type CreateStep = 1 | 2 | 3;

export default function OnboardingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [flow, setFlow] = useState<Flow>('choose');

  return (
    <main className="min-h-screen flex items-center justify-center px-4 py-8
                      bg-gradient-to-br from-slate-50 via-sky-50 to-slate-100
                      dark:bg-gradient-to-br dark:from-[#0a1420] dark:via-[#0e1f30] dark:to-[#0a1420]">
      <div className="w-full max-w-lg">
        <div className="flex justify-center mb-6">
          <img src="/kejja.svg" alt="KEJJA" className="h-16 w-auto object-contain dark:hidden" />
          <img src="/kejja-white.svg" alt="KEJJA" className="h-16 w-auto object-contain hidden dark:block" />
        </div>

        <div className="text-center mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
            Welcome to KEJJA, {user?.displayName?.split(' ')[0]}
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-300 mt-1.5">
            {flow === 'choose' && 'Join your family or create a new home.'}
            {flow === 'create' && "Let's set up your first Home."}
            {flow === 'join' && 'Enter the invite code from your family member.'}
          </p>
        </div>

        {flow === 'choose' && <ChooseScreen onCreate={() => setFlow('create')} onJoin={() => setFlow('join')} />}
        {flow === 'create' && <CreateFlow onBack={() => setFlow('choose')} onDone={(homeName) => navigate('/onboarding/celebration', { replace: true, state: { homeName } })} />}
        {flow === 'join'   && <JoinFlow   onBack={() => setFlow('choose')} onDone={(homeName) => navigate('/onboarding/celebration', { replace: true, state: { homeName } })} />}
      </div>
    </main>
  );
}

// ============================================================
// Choose screen
// ============================================================

function ChooseScreen({ onCreate, onJoin }: { onCreate: () => void; onJoin: () => void }) {
  return (
    <div className="space-y-3">
      <button
        onClick={onCreate}
        className="w-full bg-white dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-800
                   rounded-2xl shadow-sm hover:border-[#196287] dark:hover:border-sky-400
                   p-5 text-left transition-colors group"
      >
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#196287]/10 dark:bg-sky-500/15
                          text-[#196287] dark:text-sky-300 flex items-center justify-center flex-shrink-0">
            <PlusCircle className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-base text-slate-900 dark:text-white">Create a new Home</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-0.5">
              Set up your family's home from scratch. You'll be the owner and can invite members later.
            </p>
          </div>
          <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#196287] dark:group-hover:text-sky-400 flex-shrink-0 mt-1" />
        </div>
      </button>

      <button
        onClick={onJoin}
        className="w-full bg-white dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-800
                   rounded-2xl shadow-sm hover:border-[#196287] dark:hover:border-sky-400
                   p-5 text-left transition-colors group"
      >
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-xl bg-emerald-500/10 dark:bg-emerald-500/15
                          text-emerald-600 dark:text-emerald-300 flex items-center justify-center flex-shrink-0">
            <KeyRound className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-base text-slate-900 dark:text-white">Join an existing Home</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-0.5">
              A family member sent you an invite code. Enter it to join their Home.
            </p>
          </div>
          <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 flex-shrink-0 mt-1" />
        </div>
      </button>
    </div>
  );
}

// ============================================================
// Join flow
// ============================================================

function JoinFlow({ onBack, onDone }: { onBack: () => void; onDone: (homeName: string) => void }) {
  const acceptMut = useAcceptInvite();
  const [code, setCode] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!code.trim()) { setErr('Enter the invite code.'); return; }

    acceptMut.mutate(code.trim().toUpperCase(), {
      onSuccess: (r) => {
        setSuccess(`Joined ${r.homeName}! Redirecting…`);
        setTimeout(() => onDone('your home'), 1500);
      },
      onError: (e) => setErr(e.message ?? 'Invalid or expired code'),
    });
  };

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800
                    rounded-2xl shadow-xl p-6 md:p-8">
      {success ? (
        <div className="text-center space-y-3 py-4">
          <div className="h-14 w-14 rounded-full bg-emerald-100 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-300 flex items-center justify-center mx-auto">
            <Check className="w-7 h-7" />
          </div>
          <p className="text-base font-semibold text-slate-900 dark:text-white">{success}</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">
              Invite code
            </label>
            <input
              type="text" autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="ABC123DEF4"
              maxLength={16}
              className="w-full h-12 px-4 rounded-md text-center font-mono tracking-wider uppercase text-lg
                         border border-slate-300 dark:border-slate-700
                         bg-white dark:bg-slate-950
                         text-slate-900 dark:text-white
                         placeholder:text-slate-400 dark:placeholder:text-slate-500
                         focus:border-[#196287] dark:focus:border-sky-400
                         focus:ring-2 focus:ring-[#196287]/20 dark:focus:ring-sky-400/20
                         outline-none transition"
            />
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5">
              The code was generated by whoever invited you. Ask them to share it.
            </p>
          </div>

          {err && (
            <div className="flex items-start gap-2 bg-red-50 dark:bg-red-950/30
                            border border-red-200 dark:border-red-900
                            text-red-800 dark:text-red-300
                            rounded-lg px-3 py-2.5 text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{err}</span>
            </div>
          )}

          <div className="flex justify-between pt-2">
            <Button
              type="button" variant="outline"
              onClick={onBack}
              disabled={acceptMut.isPending}
              className="h-10 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
            </Button>
            <Button
              type="submit"
              disabled={acceptMut.isPending}
              className="h-10 bg-[#196287] hover:bg-[#0e415e] text-white px-5"
            >
              {acceptMut.isPending ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Joining…</>
              ) : (
                <>Join <ArrowRight className="w-4 h-4 ml-1.5" /></>
              )}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

// ============================================================
// Create flow (3-step wizard — unchanged, but now redirects to /dashboard directly)
// ============================================================

function CreateFlow({ onBack, onDone }: { onBack: () => void; onDone: (homeName: string) => void }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const createHome = useCreateHome();

  const [step, setStep] = useState<CreateStep>(1);

  const suggestedName = useMemo(() => {
    const parts = user?.displayName?.trim().split(/\s+/) ?? [];
    const last = parts.length > 1 ? parts[parts.length - 1] : parts[0] ?? 'Family';
    return `The ${last} Home`;
  }, [user?.displayName]);

  const [name, setName] = useState(suggestedName);
  const [motto, setMotto] = useState('');
  const [countryCode, setCountryCode] = useState('KE');
  const [regionText, setRegionText] = useState('');
  const [currencyCode, setCurrencyCode] = useState('KES');
  const [timezone, setTimezone] = useState('Africa/Nairobi');

  useEffect(() => {
    if (user && name === 'The Family Home') setName(suggestedName);
  }, [user, suggestedName, name]);

  const selectedCountry = findCountry(countryCode);
  useEffect(() => {
    if (selectedCountry) {
      setCurrencyCode(selectedCountry.defaultCurrency);
      setTimezone(selectedCountry.primaryTimezone);
    }
  }, [countryCode, selectedCountry]);

  const step1Valid = name.trim().length >= 2;
  const step2Valid = countryCode && currencyCode && timezone;

  async function handleSubmit() {
    try {
      await createHome.mutateAsync({
        name: name.trim(),
        motto: motto.trim() || undefined,
        country: countryCode,
        region: regionText.trim() || undefined,
        currencyCode,
        timezone,
      });
      // Wait for the homes list to be refreshed before navigating away,
      // otherwise OnboardingGate sees 0 homes and bounces us back.
      await qc.refetchQueries({ queryKey: ['me', 'homes'] });
      await qc.refetchQueries({ queryKey: ['me'] });
      onDone(name.trim());
    } catch { /* error shown via createHome.error */ }
  }

  return (
    <>
      <div className="flex items-center gap-1.5 mb-4">
        {[1, 2, 3].map((n) => (
          <div key={n}
            className={cn(
              'h-1.5 flex-1 rounded-full transition-colors',
              n <= step ? 'bg-[#196287] dark:bg-sky-400' : 'bg-slate-200 dark:bg-slate-700',
            )}
          />
        ))}
      </div>
      <p className="text-[11px] uppercase tracking-widest text-slate-500 dark:text-slate-400 text-center mb-5 font-semibold">
        Step {step} of 3 · {step === 1 ? 'About' : step === 2 ? 'Location' : 'Review'}
      </p>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl p-6 md:p-8">
        {createHome.isError && (
          <div className="flex items-start gap-2 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 text-red-800 dark:text-red-300 rounded-lg px-3 py-2.5 mb-5 text-sm">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{(createHome.error as Error)?.message || 'Failed to create home. Try again?'}</span>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-5">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">About your Home</h2>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">
                What will you call your Home?
              </label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="e.g. The Okoth Home" autoFocus
                className="w-full h-11 px-3 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:border-[#196287] dark:focus:border-sky-400 focus:ring-2 focus:ring-[#196287]/20 dark:focus:ring-sky-400/20 outline-none transition"
              />
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5">This is how your family will see it across KEJJA</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">
                Family motto <span className="text-slate-400 dark:text-slate-500 font-normal">(optional)</span>
              </label>
              <input type="text" value={motto} onChange={(e) => setMotto(e.target.value)}
                placeholder="e.g. Ujima — Collective Work and Responsibility"
                className="w-full h-11 px-3 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:border-[#196287] dark:focus:border-sky-400 focus:ring-2 focus:ring-[#196287]/20 dark:focus:ring-sky-400/20 outline-none transition"
              />
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5">Something that reminds your family what you stand for</p>
            </div>
            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={onBack}
                className="h-10 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200">
                <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
              </Button>
              <Button onClick={() => setStep(2)} disabled={!step1Valid}
                className="h-10 bg-[#196287] hover:bg-[#0e415e] text-white px-5">
                Next <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Where you are</h2>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">Country</label>
              <select value={countryCode} onChange={(e) => setCountryCode(e.target.value)}
                className="w-full h-11 px-3 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-white focus:border-[#196287] dark:focus:border-sky-400 focus:ring-2 focus:ring-[#196287]/20 dark:focus:ring-sky-400/20 outline-none transition">
                {COUNTRIES.map((c) => (<option key={c.code} value={c.code}>{c.flag}  {c.name}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">
                Region / City <span className="text-slate-400 dark:text-slate-500 font-normal">(optional)</span>
              </label>
              <input type="text" value={regionText} onChange={(e) => setRegionText(e.target.value)}
                placeholder="e.g. Nakuru, Kisumu, Kampala"
                className="w-full h-11 px-3 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:border-[#196287] dark:focus:border-sky-400 focus:ring-2 focus:ring-[#196287]/20 dark:focus:ring-sky-400/20 outline-none transition"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">Currency</label>
                <select value={currencyCode} onChange={(e) => setCurrencyCode(e.target.value)}
                  className="w-full h-11 px-3 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-white focus:border-[#196287] dark:focus:border-sky-400 focus:ring-2 focus:ring-[#196287]/20 dark:focus:ring-sky-400/20 outline-none transition">
                  {CURRENCIES.map((c) => (<option key={c.code} value={c.code}>{c.code} — {c.name}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5">Timezone</label>
                <select value={timezone} onChange={(e) => setTimezone(e.target.value)}
                  className="w-full h-11 px-3 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-white focus:border-[#196287] dark:focus:border-sky-400 focus:ring-2 focus:ring-[#196287]/20 dark:focus:ring-sky-400/20 outline-none transition">
                  {Object.entries(timezonesByRegion()).map(([region, zones]) => (
                    <optgroup key={region} label={region}>
                      {zones.map((tz) => (<option key={tz.iana} value={tz.iana}>{tz.label}</option>))}
                    </optgroup>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(1)}
                className="h-10 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200">
                <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
              </Button>
              <Button onClick={() => setStep(3)} disabled={!step2Valid}
                className="h-10 bg-[#196287] hover:bg-[#0e415e] text-white px-5">
                Next <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Ready to go?</h2>
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/50 p-5">
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-xl bg-[#196287]/10 dark:bg-sky-500/15 text-[#196287] dark:text-sky-300 flex items-center justify-center flex-shrink-0">
                  <HomeIcon className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-lg text-slate-900 dark:text-white">{name}</h3>
                  {motto && <p className="text-sm text-slate-600 dark:text-slate-400 italic mt-0.5">"{motto}"</p>}
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-800 space-y-2 text-sm">
                <Row label="📍 Location">{selectedCountry?.flag} {selectedCountry?.name}{regionText ? ` · ${regionText}` : ''}</Row>
                <Row label="💰 Currency">{currencyCode} <span className="text-slate-500 dark:text-slate-400">— {findCurrency(currencyCode)?.name}</span></Row>
                <Row label="🕐 Timezone">{TIMEZONES.find((t) => t.iana === timezone)?.label ?? timezone}</Row>
              </div>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 text-center">You can change any of this later in Settings</p>
            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(2)} disabled={createHome.isPending}
                className="h-10 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200">
                <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
              </Button>
              <Button onClick={handleSubmit} disabled={createHome.isPending}
                className="h-10 bg-[#196287] hover:bg-[#0e415e] text-white px-5">
                {createHome.isPending
                  ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Creating…</>
                  : <><Sparkles className="w-4 h-4 mr-1.5" /> Create my Home</>}
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-slate-500 dark:text-slate-400 text-xs font-medium uppercase tracking-wide">{label}</span>
      <span className="text-slate-900 dark:text-white text-right">{children}</span>
    </div>
  );
}
